import { computed, ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import type { SurveyContext, SurveyContextSource } from "@/types";
import { captureEvent } from "@/lib/analytics";
import { getClientId } from "@utils/clientId";
import { ACCEPTED_FAMILIES, MAX_FILE_SIZE, MAX_SOURCES, isAccepted } from "@/components/Survey/Context/contextSources";
import { formatFileSize } from "@utils/media";

const errorMessage = (error: unknown) => (Array.isArray(error) ? error.join(", ") : String(error));

export const useSurveyContextStore = defineStore("surveyContext", () => {
  const context = ref<SurveyContext | null>(null);
  const draft = ref<string | null>(null);
  const saving = ref(false);
  const hasUnsavedChanges = computed(() => draft.value !== null);

  async function fetch(surveyToken: string) {
    const { data, error } = await useMyFetch(`/surveys/${surveyToken}/context`).json<SurveyContext>();
    if (error.value) throw new Error(errorMessage(error.value));

    context.value = data.value;
  }

  async function createSource(surveyToken: string, body: FormData) {
    const { data, error } = await useMyFetch(`/surveys/${surveyToken}/context_sources`)
      .post(body)
      .json<SurveyContextSource>();
    if (error.value) throw new Error(errorMessage(error.value));

    if (context.value && data.value) context.value.sources.push(data.value);
    return data.value;
  }

  async function addFiles(surveyToken: string, files: File[]) {
    const failures: string[] = [];
    let added = 0;
    for (const file of files) {
      if ((context.value?.sources.length ?? 0) >= MAX_SOURCES) {
        failures.push(`${file.name}: a survey holds at most ${MAX_SOURCES} sources`);
        continue;
      }
      if (!isAccepted(file)) {
        failures.push(
          `${file.name}: file type not accepted (${ACCEPTED_FAMILIES}; unzip archives and add their files)`,
        );
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        captureEvent("survey_context_file_rejected", { size_mb: Math.ceil(file.size / 1024 / 1024) });
        failures.push(`${file.name}: larger than ${formatFileSize(MAX_FILE_SIZE)}`);
        continue;
      }

      const body = new FormData();
      body.append("file", file);
      try {
        await createSource(surveyToken, body);
        added += 1;
      } catch (error) {
        failures.push(`${file.name}: ${(error as Error).message}`);
      }
    }
    if (added > 0) captureEvent("survey_context_source_added", { kind: "file", sources_count: added });
    return failures;
  }

  async function addText(surveyToken: string, name: string, textContent: string) {
    const body = new FormData();
    body.append("survey_context_source[name]", name);
    body.append("survey_context_source[text_content]", textContent);
    await createSource(surveyToken, body);
    captureEvent("survey_context_source_added", { kind: "text", sources_count: 1 });
  }

  async function fetchSource(surveyToken: string, sourceToken: string) {
    const { data, error } = await useMyFetch(
      `/surveys/${surveyToken}/context_sources/${sourceToken}`,
    ).json<SurveyContextSource>();
    if (error.value || !data.value) throw new Error(errorMessage(error.value));

    return data.value;
  }

  async function updateText(surveyToken: string, sourceToken: string, name: string, textContent: string) {
    const { data, error } = await useMyFetch(`/surveys/${surveyToken}/context_sources/${sourceToken}`)
      .patch({ survey_context_source: { name, text_content: textContent } })
      .json<SurveyContextSource>();
    if (error.value) throw new Error(errorMessage(error.value));

    captureEvent("survey_context_source_updated");
    if (context.value && data.value) {
      const updated = data.value;
      context.value.sources = context.value.sources.map((source) => (source.token === sourceToken ? updated : source));
    }
  }

  async function removeSource(surveyToken: string, sourceToken: string) {
    const { error } = await useMyFetch(`/surveys/${surveyToken}/context_sources/${sourceToken}`).delete();
    if (error.value) throw new Error(errorMessage(error.value));

    captureEvent("survey_context_source_deleted");
    if (context.value) context.value.sources = context.value.sources.filter((source) => source.token !== sourceToken);
  }

  async function save(surveyToken: string) {
    if (draft.value === null) return;

    saving.value = true;
    try {
      const { data, error } = await useMyFetch(`/surveys/${surveyToken}/context`)
        .patch({ survey_context: { content: draft.value } })
        .json<SurveyContext>();
      if (error.value) throw new Error(errorMessage(error.value));

      captureEvent("survey_context_saved", { sources_count: data.value?.sources.length ?? 0 });
      draft.value = null;
      context.value = data.value;
    } finally {
      saving.value = false;
    }
  }

  async function generate(surveyToken: string) {
    const { data, error, statusCode } = await useMyFetch(`/surveys/${surveyToken}/context/generate`)
      .post({ client_id: getClientId() })
      .json<SurveyContext>();
    if (error.value) throw new Error(errorMessage(error.value));

    if (statusCode.value === 202)
      captureEvent("survey_context_generated", { sources_count: data.value?.sources.length ?? 0 });
    context.value = data.value;
  }

  let refreshing: Promise<void> | null = null;
  function refreshIfGenerating(surveyToken: string) {
    if (context.value?.status !== "generating") return Promise.resolve();

    refreshing ??= fetch(surveyToken).finally(() => (refreshing = null));
    return refreshing;
  }

  function reset() {
    context.value = null;
    draft.value = null;
  }

  return {
    context,
    draft,
    saving,
    hasUnsavedChanges,
    fetch,
    refreshIfGenerating,
    addFiles,
    addText,
    fetchSource,
    updateText,
    removeSource,
    save,
    generate,
    reset,
  };
});
